
import TextField from '@mui/material/TextField';
import { useLocalizedStrings } from '../../localization/LocalizedStringsProvider';
import Dialog from "../common/dialog/Dialog";
import { useForm, type SubmitHandler } from 'react-hook-form';
import Spacer from '../common/Spacer';
import { useMutation } from '@tanstack/react-query';
import { useEffect } from 'react';
import { organizationApi } from '@/api/account-management/organizationApi';
import { type User } from '@/models/account-management/User';
import { useToast } from '../common/toast/ToastProvider';
import { isValidEmail } from '../../validation/fieldValidators';

export interface InviteUserDialogProps {
    organizationId: string;
    open: boolean;
    onDone: () => void;
    onCancel: (event: React.MouseEvent<HTMLButtonElement>) => void;
}

const InviteUserDialog = ({ organizationId, open, onDone, onCancel }: InviteUserDialogProps) => {   
    // Explicit defaults, because reset() resets TO the defaults — and with none declared there was nothing
    // to reset to, so the fields kept whatever had been typed. See the effect below.
    const { reset, register, handleSubmit: onFormSubmit, formState } = useForm<User>({
        defaultValues: { firstName: '', lastName: '', email: '' },
    });
    const { mutateAsync: saveUser, isPending: isSaving } = useMutation({ mutationFn: (user: User) => organizationApi.inviteUser(organizationId, user), onSuccess: () => handleSaveSuccess(), onError: () => handleSaveError() });
    const toast = useToast();
    const strings = useLocalizedStrings();

    // The dialog is never unmounted, so a cancelled invite would otherwise keep its values and show them
    // again the next time it is opened.
    //
    // This reset was added once already and did nothing, because reset() resets TO the form's defaultValues
    // and none were declared — there was nothing to reset the fields to, so they kept what had been typed.
    // The defaultValues above are what make this line work; do not remove them.
    //
    // The other dialogs in this area (UserList, RoleList, OrganizationList) are safe for a different reason:
    // they call reset(createUser()) and friends when OPENING, so they set values explicitly every time. Their
    // close-time reset(undefined) is the same no-op this one was, it just never mattered.
    useEffect(() => {
        if (!open) reset();
    }, [open, reset]);

    const handleSubmit: SubmitHandler<User> = async (data) => {
        console.log(data);                
        saveUser(data);
    }

    const handleSaveSuccess = async () => {
		toast({ severity: "success", message: strings.userInviteSuccess });		 
        reset();
        onDone();		
	}

	const handleSaveError = () => {
		toast({ severity: "error", message: strings.userInviteError });
	}

    return (
        <form onSubmit={onFormSubmit(handleSubmit)} id="dialog-invite-user-form">
            <Dialog
                open={open}
                title={strings.inviteUser}
                formId="dialog-invite-user-form"
                formIsDirty={formState.isDirty}
                saving={isSaving}
                saveButtonTitle={strings.invite}
                onCancel={onCancel}
                cancelButtonTitle={strings.cancel}
                pad={true}
            >
                <TextField
                    id="firstName"
                    label={strings.firstName}
                    variant="outlined"
                    sx={{ width: 400 }}
                    {...register("firstName", {
                        required: strings.validationFirstNameRequired
                    })}
                    error={!!formState.errors.firstName}
                    helperText={formState.errors.firstName?.message || " "}
                />
                <Spacer />
                <TextField
                    id="lastName"
                    label={strings.lastName}
                    variant="outlined"
                    sx={{ width: 400 }}
                    {...register("lastName", {
                        required: strings.validationLastNameRequired
                    })}
                    error={!!formState.errors.lastName}
                    helperText={formState.errors.lastName?.message || " "}
                />
                <Spacer />
                <TextField
                    id="email"
                    label={strings.email}
                    variant="outlined"
                    sx={{ width: 400 }}
                    {...register("email", {
                        required: strings.validationEmailRequired,
                        // Format is only worth checking once something has been entered;
                        // `required` already covers the empty case.
                        validate: (v?: string) => !v?.trim() || isValidEmail(v) || strings.validationEmailInvalid,
                    })}
                    error={!!formState.errors.email}
                    helperText={formState.errors.email?.message || " "}
                />
            </Dialog>
            </form>
    );
};

export default InviteUserDialog;